import {
  ClientGroupType,
  CreateClientGroupInput,
  GetClientPageQuery,
  GetClientPageQueryVariables,
} from "src/generated/graphql";
import { useForm } from "@mantine/form";
import {
  Button,
  Text,
  Group,
  useMantineTheme,
  SelectItem,
} from "@mantine/core";
import Select from "src/components/Select";
import { useState, useEffect } from "react";
import { useAddClientToGroupMutation } from "src/generated/graphql";
import { debounce } from "src/utils/utils";
import { useApolloClient } from "@apollo/client";
import {
  GetClientGroupMany,
  GetClientPage,
} from "src/api/graphql/queries/Client";

interface CreateClientGroupProps<T> {
  handleClose: () => void;
  clientId?: string;
  clientGroupId?: string;
  refetchClientGroups?: () => void;
}

type IFormProps = Omit<CreateClientGroupInput, "primaryClient"> & {
  name: string;
  primaryClient: string;
  clientGroupType: ClientGroupType;
};

const clientGroupInitialValues = {
  name: "",
  primaryClient: "",
  clientGroupType: ClientGroupType.Product,
};

export enum FormTypes {
  NewGroup = "New Group",
  ExistingGroup = "Existing Group",
}

export const AddClientToGroupForm = <T,>({
  clientId,
  clientGroupId,

  handleClose,
}: CreateClientGroupProps<T>) => {
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [searchedTypeList, setSearchTypeList] = useState<SelectItem[]>([]);
  const [searchedClient, setSearchedClient] = useState<string | null>("");
  const [searchedValue, setSearchedValue] = useState("");

  const form = useForm<IFormProps>({
    initialValues: clientGroupInitialValues,
  });

  const refetchQuery = [
    { query: GetClientGroupMany, variables: { id: clientId } },
  ];
  const [addClientToGroup, { loading: updateLoading, error }] =
    useAddClientToGroupMutation({
      onCompleted: () => {
        handleClose();
      },
      refetchQueries: refetchQuery,
    });

  const handleSubmit = () => {
    if (searchedClient && clientGroupId) {
      addClientToGroup({
        variables: {
          clientId: searchedClient,
          clientGroupId: clientGroupId,
        },
      });
    }
  };

  const onSearchList = (searchValue: string, isFirstTime = false) => {
    setSearchedValue(searchValue);
    const isSameItem = searchedTypeList.some(
      (searchListItem) => searchListItem.value === searchedClient
    );
    if ((!searchValue && !isFirstTime) || isSameItem) {
      return;
    }
    const variables = {
      pagination: {
        count: 10,
        page: 1,
        filters: [
          {
            field: "name",
            type: "text",
            value: searchValue,
            ...{
              parentRelationField: "user",
            },
          },
        ],
      },
    };

    query<GetClientPageQuery, GetClientPageQueryVariables>({
      query: GetClientPage,
      variables,
    }).then(({ data }) => {
      const list = data?.clientPage?.items?.map((val) => {
        return {
          label: `${val?.user.firstName} ${val?.user.lastName}`,
          value: val?.id,
        };
      });
      setSearchTypeList(list || []);
    });
  };

  const onDebouncedSearch = debounce(onSearchList, 500);

  useEffect(() => {
    onSearchList("", true);
  }, []);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Text>
        Search and select the Client you want to add to this existing group,
      </Text>
      <Select
        data={searchedTypeList || []}
        placeholder={"Search"}
        searchable
        clearable
        maxDropdownHeight={500}
        nothingFound={
          searchedValue ? "No result found!" : "Please type something!"
        }
        onSearchChange={onDebouncedSearch}
        onChange={setSearchedClient}
      />

      {!updateLoading && error ? (
        <Text color="red" my={10}>
          {error.message}
        </Text>
      ) : null}

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={handleClose}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          disabled={updateLoading}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={updateLoading}
        >
          Add
        </Button>
      </Group>
    </form>
  );
};
